/**
 * Humanizes a string
 * From https://stackoverflow.com/a/28339742
 * and https://stackoverflow.com/a/17572933
 * and https://stackoverflow.com/a/21149072
 * @returns {string} Humanized string
 */
export const humanize = (text: string | undefined): string => {
  if (!text) {
    return '';
  }

  return camelCaseToWords(text);
};

export const camelCaseToWords = (text: string): string => {
  if (!text) {
    return '';
  }

  // Use a regular expression to match words, including parentheses
  const matches = text.match(/([A-Z]?[a-z]+|[A-Z]+|\(|\)|\d+)/g);

  if (!matches) {
    return '';
  }

  const words = matches
    .filter(match => match !== null && match !== undefined)
    .map(word => {
      if (word.length <= 1) {
        return word;
      }

      return (
        word[0].toUpperCase() +
        (word[1] === word[1].toLocaleLowerCase() ? word.substring(1).toLowerCase() : word.substring(1).toUpperCase())
      );
    });

  if (!words.length) {
    return '';
  }

  return words.join(' ');
};

/**
 * Splits camelCase strings into words while correctly preserving acronyms.
 *
 * The key difference from camelCaseToWords: uses a lookahead so that a run
 * of uppercase letters is only consumed up to (but not including) the capital
 * that starts the next word.
 *
 * Examples:
 *   IDCheck           → "ID Check"     (not "IDC Heck")
 *   GDCEnabled        → "GDC Enabled"  (not "GDCE Nabled")
 *   DataSourceNGA     → "Data Source NGA"
 *   kyxGovtIDCheckGdcDataSourceNGA → "Kyx Govt ID Check Gdc Data Source NGA"
 */
export const camelCaseToWordsWithAcronyms = (text: string): string => {
  if (!text) {
    return '';
  }

  // Pattern breakdown (tried in order per position):
  //   [A-Z]+(?=[A-Z][a-z])  – uppercase run before a Title-cased word  (e.g. "ID" in "IDCheck")
  //   [A-Z]?[a-z]+          – optional leading capital + lowercase run  (e.g. "Check", "kyx")
  //   [A-Z]+$               – uppercase run at end of string            (e.g. "NGA")
  //   \d+                   – numeric run                               (e.g. "3")
  const matches = text.match(/[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z]+|[A-Z]+$|\d+/g);

  if (!matches) {
    return '';
  }

  const words = matches
    .filter(Boolean)
    .map(word => {
      if (word.length <= 1) {
        return word;
      }

      return (
        word[0].toUpperCase() +
        (word[1] === word[1].toLocaleLowerCase() ? word.substring(1).toLowerCase() : word.substring(1).toUpperCase())
      );
    });

  if (!words.length) {
    return '';
  }

  return words.join(' ');
};

/**
 * Converts a camelCase string to a human-readable label, correctly
 * preserving acronyms (e.g. ID, GDC, NGA).
 *
 * Use this instead of `humanize` wherever setting keys or API property
 * names contain embedded acronyms.
 */
export const camelToLabel = (text: string | undefined): string => {
  if (!text) {
    return '';
  }

  return camelCaseToWordsWithAcronyms(text);
};
